
import { db } from "@/integrations/cloudflare/client";
import { NamePrefix, NameSuffix, LastName, ImportResult } from "./types";
import { invalidateCache } from "./nameCache";

// Helper function to get all prefixes from the database
export const getNamePrefixes = async (): Promise<NamePrefix[]> => {
  const { data, error } = await db
    .from('elf_name_prefixes')
    .select('id, prefix, category')
    .order('prefix');
  
  if (error) {
    console.error("Error fetching prefixes:", error);
    return [];
  }
  
  return data || [];
};

// Helper function to get all suffixes from the database
export const getNameSuffixes = async (): Promise<NameSuffix[]> => {
  const { data, error } = await db
    .from('elf_name_suffixes')
    .select('id, suffix, category')
    .order('suffix');
  
  if (error) {
    console.error("Error fetching suffixes:", error);
    return [];
  }
  
  return data || [];
};

// Helper function to get all last names from the database
export const getLastNames = async (): Promise<LastName[]> => {
  const { data, error } = await db
    .from('elf_last_names')
    .select('id, last_name, category')
    .order('last_name');
  
  if (error) {
    console.error("Error fetching last names:", error);
    return [];
  }
  
  return data || [];
};

// Functions to add new name components
export const addNamePrefix = async (prefix: string, category?: string): Promise<boolean> => {
  const { error } = await db
    .from('elf_name_prefixes')
    .insert({
      prefix,
      category: category || 'custom'
    });
  
  if (error) {
    console.error("Error adding prefix:", error);
    return false;
  }
  
  // Invalidate cache
  invalidateCache();
  return true;
};

export const addNameSuffix = async (suffix: string, category?: string): Promise<boolean> => {
  const { error } = await db
    .from('elf_name_suffixes')
    .insert({
      suffix,
      category: category || 'custom'
    });
  
  if (error) {
    console.error("Error adding suffix:", error);
    return false;
  }
  
  // Invalidate cache
  invalidateCache();
  return true;
};

export const addLastName = async (lastName: string, category?: string): Promise<boolean> => {
  const { error } = await db
    .from('elf_last_names')
    .insert({
      last_name: lastName,
      category: category || 'custom'
    });
  
  if (error) {
    console.error("Error adding last name:", error);
    return false;
  }
  
  // Invalidate cache
  invalidateCache();
  return true;
};

// Functions to delete name components
export const deleteNamePrefix = async (id: string): Promise<boolean> => {
  const { error } = await db
    .from('elf_name_prefixes')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error("Error deleting prefix:", error);
    return false;
  }
  
  // Invalidate cache
  invalidateCache();
  return true;
};

export const deleteNameSuffix = async (id: string): Promise<boolean> => {
  const { error } = await db
    .from('elf_name_suffixes')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error("Error deleting suffix:", error);
    return false;
  }
  
  // Invalidate cache
  invalidateCache();
  return true;
};

export const deleteLastName = async (id: string): Promise<boolean> => {
  const { error } = await db
    .from('elf_last_names')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error("Error deleting last name:", error);
    return false;
  }
  
  // Invalidate cache
  invalidateCache();
  return true;
};

// Functions to import name components in bulk
export const importNamePrefixes = async (
  prefixes: {name: string, category?: string}[]
): Promise<ImportResult> => {
  let success = 0;
  let failed = 0;
  
  // We'll perform individual inserts
  for (const item of prefixes) {
    const result = await addNamePrefix(item.name, item.category);
    if (result) {
      success++;
    } else {
      failed++;
    }
  }
  
  return { success, failed };
};

export const importNameSuffixes = async (
  suffixes: {name: string, category?: string}[]
): Promise<ImportResult> => {
  let success = 0;
  let failed = 0;
  
  // We'll perform individual inserts
  for (const item of suffixes) {
    const result = await addNameSuffix(item.name, item.category);
    if (result) {
      success++;
    } else {
      failed++;
    }
  }
  
  return { success, failed };
};

export const importLastNames = async (
  lastNames: {name: string, category?: string}[]
): Promise<ImportResult> => {
  let success = 0;
  let failed = 0;
  
  // We'll perform individual inserts
  for (const item of lastNames) {
    const result = await addLastName(item.name, item.category);
    if (result) {
      success++;
    } else {
      failed++;
    }
  }
  
  return { success, failed };
};
